import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { hasPermission } from "../utils/rbac";

export function usePermissionGuard() {
  const [open, setOpen] = useState(false);
  const [actionName, setActionName] = useState("perform this action");

  const guardAction = useCallback((permission, actionLabel, callback) => {
    if (hasPermission(permission)) {
      callback?.();
      return true;
    }
    setActionName(actionLabel);
    setOpen(true);
    return false;
  }, []);

  const PermissionDialog = () => (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="z-[9999]">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-black">
            Permission denied
          </AlertDialogTitle>
          <AlertDialogDescription className="text-black">
            You don&apos;t have permission to {actionName}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            className="bg-[#2F7F57] hover:bg-[#256845] text-white"
            onClick={() => setOpen(false)}
          >
            OK
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { guardAction, PermissionDialog, isAllowed: hasPermission };
}

export const deniedActionClass = (allowed) =>
  allowed ? "" : "opacity-50 cursor-not-allowed";

/** Show permission dialog once when a route requires a permission the user lacks. */
export function useRoutePermissionGuard(permission, actionLabel) {
  const { guardAction, PermissionDialog, isAllowed } = usePermissionGuard();
  const allowed = permission ? isAllowed(permission) : true;
  const shownRef = useRef(false);

  useEffect(() => {
    if (!permission || allowed || shownRef.current) return;
    shownRef.current = true;
    guardAction(permission, actionLabel);
  }, [permission, allowed, actionLabel, guardAction]);

  return { allowed, guardAction, PermissionDialog, isAllowed };
}
